/**
 * Brand Information Form Component
 * Fifth step of enhanced manufacturer registration
 */

import React, { useState, useEffect } from 'react';
import { Button, Input } from '../ui';
import { BrandInformation } from '../../services/registration.service';

interface BrandInformationFormProps {
  data?: BrandInformation;
  onSave: (data: BrandInformation) => Promise<void>;
  onNext: () => void;
  onPrevious: () => void;
  saving: boolean;
}

const INDUSTRY_CATEGORIES: Record<string, string[]> = {
  'Food & Beverages': ['Food Products', 'Consumer Goods', 'Agricultural Products', 'Other'],
  'Pharmaceuticals': ['Healthcare Products', 'Chemical Products', 'Industrial Products', 'Other'],
  'FMCG': ['Consumer Goods', 'Food Products', 'Cosmetics', 'Other'],
  'Auto Parts': ['Automotive Parts', 'Industrial Products', 'Electronic Components', 'Other'],
  'Electronics': ['Electronic Components', 'Consumer Goods', 'Industrial Products', 'Other'],
  'Textiles': ['Textile Products', 'Consumer Goods', 'Other'],
  'Chemicals': ['Chemical Products', 'Industrial Products', 'Agricultural Products', 'Other'],
  'Cosmetics': ['Consumer Goods', 'Chemical Products', 'Healthcare Products', 'Other'],
  'Agriculture': ['Agricultural Products', 'Food Products', 'Industrial Products', 'Other'],
  'Other': ['Consumer Goods', 'Industrial Products', 'Healthcare Products', 'Food Products', 'Electronic Components', 'Automotive Parts', 'Textile Products', 'Chemical Products', 'Agricultural Products', 'Other']
};

const INDUSTRIES = Object.keys(INDUSTRY_CATEGORIES);

export function BrandInformationForm({ data, onSave, onNext, onPrevious, saving }: BrandInformationFormProps) {
  const [formData, setFormData] = useState<BrandInformation>({
    brandNames: [''],
    trademarkStatus: 'NOT_REGISTERED',
    trademarkNumber: '',
    industry: '',
    productCategories: [],
    productSkus: [],
    monthlyProductionVolume: undefined
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (data) {
      setFormData({
        ...data,
        brandNames: data.brandNames.length > 0 ? data.brandNames : ['']
      });
    }
  }, [data]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    const validBrandNames = formData.brandNames.filter(name => name.trim());
    if (validBrandNames.length === 0) {
      newErrors.brandNames = 'At least one brand name is required';
    }

    if (!formData.industry.trim()) {
      newErrors.industry = 'Industry is required';
    }

    if (formData.productCategories.length === 0) {
      newErrors.productCategories = 'At least one product category is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleBrandNameChange = (index: number, value: string) => {
    const newBrandNames = [...formData.brandNames];
    newBrandNames[index] = value;
    setFormData(prev => ({ ...prev, brandNames: newBrandNames }));
  };

  const addBrandName = () => {
    setFormData(prev => ({
      ...prev,
      brandNames: [...prev.brandNames, '']
    }));
  };

  const removeBrandName = (index: number) => {
    if (formData.brandNames.length > 1) {
      const newBrandNames = formData.brandNames.filter((_, i) => i !== index);
      setFormData(prev => ({ ...prev, brandNames: newBrandNames }));
    }
  };

  const handleProductCategoryChange = (category: string, checked: boolean) => {
    if (checked) {
      setFormData(prev => ({
        ...prev,
        productCategories: [...prev.productCategories, category]
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        productCategories: prev.productCategories.filter(c => c !== category)
      }));
    }
  };

  const suggestedCategories = formData.industry ? INDUSTRY_CATEGORIES[formData.industry] || [] : [];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    const submitData = {
      ...formData,
      brandNames: formData.brandNames.filter(name => name.trim())
    };

    try {
      await onSave(submitData);
      onNext();
    } catch (error) {
      // Error handling is done in parent component
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Brand Names */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Brand Names *
        </label>
        {formData.brandNames.map((brandName, index) => (
          <div key={index} className="flex gap-2 mb-2">
            <Input
              value={brandName}
              onChange={(e) => handleBrandNameChange(index, e.target.value)}
              placeholder={`Brand name ${index + 1}`}
              className="flex-1"
            />
            {formData.brandNames.length > 1 && (
              <Button
                type="button"
                variant="outline"
                onClick={() => removeBrandName(index)}
                className="px-3"
              >
                Remove
              </Button>
            )}
          </div>
        ))}
        <Button
          type="button"
          variant="outline"
          onClick={addBrandName}
          className="mt-2"
        >
          Add Another Brand
        </Button>
        {errors.brandNames && (
          <p className="mt-1 text-sm text-red-600">{errors.brandNames}</p>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Trademark Status */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Trademark Status *
          </label>
          <select
            value={formData.trademarkStatus}
            onChange={(e) => setFormData(prev => ({ ...prev, trademarkStatus: e.target.value as any }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-900 bg-white"
            style={{ color: '#111827' }}
          >
            <option value="NOT_REGISTERED">Not Registered</option>
            <option value="APPLIED">Applied</option>
            <option value="REGISTERED">Registered</option>
          </select>
        </div>

        {/* Trademark Number */}
        {formData.trademarkStatus !== 'NOT_REGISTERED' && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Trademark Number
            </label>
            <Input
              value={formData.trademarkNumber || ''}
              onChange={(e) => setFormData(prev => ({ ...prev, trademarkNumber: e.target.value }))}
              placeholder="Trademark registration/application number"
            />
          </div>
        )}

        {/* Industry */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Industry *
          </label>
          <select
            value={formData.industry}
            onChange={(e) => {
              const industry = e.target.value;
              const defaults = INDUSTRY_CATEGORIES[industry] || [];
              setFormData(prev => ({
                ...prev,
                industry,
                productCategories: prev.productCategories.length ? prev.productCategories : defaults
              }));
            }}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-900 bg-white"
            style={{ color: '#111827' }}
          >
            <option value="">Select Industry</option>
            {INDUSTRIES.map(industry => (
              <option key={industry} value={industry}>
                {industry}
              </option>
            ))}
          </select>
          {errors.industry && (
            <p className="mt-1 text-sm text-red-600">{errors.industry}</p>
          )}
        </div>

        {/* Monthly Production Volume */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Monthly Production Volume (Units)
            <span className="text-gray-500 text-xs ml-1">(Optional)</span>
          </label>
          <Input
            type="number"
            value={formData.monthlyProductionVolume || ''}
            onChange={(e) => setFormData(prev => ({ 
              ...prev, 
              monthlyProductionVolume: e.target.value ? parseInt(e.target.value) : undefined 
            }))}
            placeholder="Approximate monthly production"
            min="0"
          />
        </div>
      </div>

      {/* Product Categories */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Product Categories *
        </label>
        {formData.industry && (
          <div className="mb-3">
            <p className="text-xs text-gray-500 mb-1">Suggested for {formData.industry}</p>
            <div className="flex flex-wrap gap-2">
              {suggestedCategories.map((cat) => (
                <button
                  key={cat}
                  type="button"
                  className={`px-3 py-1 rounded-full text-xs border ${
                    formData.productCategories.includes(cat)
                      ? 'bg-blue-50 text-blue-700 border-blue-300'
                      : 'bg-white text-gray-700 border-gray-300 hover:border-blue-300'
                  }`}
                  onClick={() => handleProductCategoryChange(cat, !formData.productCategories.includes(cat))}
                >
                  {cat}
                </button>
              ))}
            </div>
          </div>
        )}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
          {Array.from(new Set(Object.values(INDUSTRY_CATEGORIES).flat())).map(category => (
            <label key={category} className="flex items-center">
              <input
                type="checkbox"
                checked={formData.productCategories.includes(category)}
                onChange={(e) => handleProductCategoryChange(category, e.target.checked)}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <span className="ml-2 text-sm text-gray-700">{category}</span>
            </label>
          ))}
        </div>
        {errors.productCategories && (
          <p className="mt-1 text-sm text-red-600">{errors.productCategories}</p>
        )}
      </div>

      {/* Information Box */}
      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
        <div className="flex">
          <svg className="w-5 h-5 text-green-400 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-green-800">Brand Protection</h3>
            <div className="text-sm text-green-700 mt-1">
              <p>• This information helps us establish intellectual property rights</p>
              <p>• Brand names will be used for anti-counterfeit protection</p>
              <p>• Trademark information helps verify brand ownership</p>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Buttons */}
      <div className="flex justify-between">
        <Button
          type="button"
          variant="outline"
          onClick={onPrevious}
        >
          Previous
        </Button>
        <Button
          type="submit"
          disabled={saving}
          className="px-6 py-2"
        >
          {saving ? 'Saving...' : 'Save & Continue'}
        </Button>
      </div>
    </form>
  );
}
